// @flow

import * as React from 'react';
import {useRef, useState} from 'react';
import Box from 'sail/Box';
import type {Props as BoxProps} from 'sail/Box';
import {useContentFrame} from 'docs/shell/content/ContentFrameProvider';
import {
  variants,
  transition,
  useContentAnimation,
} from 'docs/shell/content/ContentAnimationProvider';
import {ContentGroupItem} from 'docs/util';
import {motion} from 'framer-motion';

import {ContentGroupContext} from './ContentGroupContext';

export type UncontrolledContentGroupProps = {|
  // $FlowFixMe
  ...BoxProps,
  className?: string,
  contentProps?: BoxProps,
  items: Array<ContentGroupItem>,
  initialSelectedId?: ?string,
  onItemSelected?: (item: ContentGroupItem) => void,
  selector?: React.Node,
|};

const getInitialSelectedItem = (items, initialSelectedId): ContentGroupItem => {
  if (initialSelectedId) {
    const selectedItem = items.find((item) => item.id === initialSelectedId);
    if (selectedItem) {
      return selectedItem;
    }
  }
  return items[0];
};

/**
 * An UncontrolledContentGroup is a ContentGroup that stores the selected item in
 * local state. It accepts the id for the item that should be selected by default
 * via the initialSelectedId prop, but changing this prop on subsequent renders
 * will NOT change the selected item in the group.
 *
 * If you need to manage the state for the selected item yourself (for example,
 * to keep multiple ContentGroups in sync), you should use a ControlledContentGroup.
 */
export const UncontrolledContentGroup = ({
  className,
  contentProps = {},
  initialSelectedId,
  items,
  onItemSelected,
  selector,
  ...props
}: UncontrolledContentGroupProps) => {
  const ref = useRef<?HTMLElement>();
  const {pinAt} = useContentFrame();
  const {direction, setAnimationDirection} = useContentAnimation();

  if (items.length === 0) {
    throw new Error('Content groups must contain at least one item');
  }

  const [selectedItem, setSelectedItem] = useState(
    getInitialSelectedItem(items, initialSelectedId),
  );

  const handleItemSelected = (item) => {
    if (ref.current) {
      pinAt(ref.current);
    }

    // Set which direction to animate the tabs
    const prevIndex = items.findIndex((i) => i.id === selectedItem.id);
    const selectedIndex = items.findIndex((i) => i.id === item.id);
    setAnimationDirection(prevIndex, selectedIndex);

    setSelectedItem(item);

    if (onItemSelected) {
      onItemSelected(item);
    }
  };

  const context = {
    items,
    selectedItem,
    onItemSelected: handleItemSelected,
  };

  return (
    <ContentGroupContext.Provider value={context}>
      <Box {...props} boxRef={ref} className={className}>
        {selector}
        <Box
          key={selectedItem.id}
          padding={{vertical: 12}}
          {...contentProps}
          style={{overflow: 'hidden'}}
        >
          <motion.div
            key="UncontrolledContentGroup"
            initial="enter"
            animate="center"
            exit="exit"
            variants={variants}
            custom={direction}
            transition={transition}
          >
            {selectedItem.content}
          </motion.div>
        </Box>
      </Box>
    </ContentGroupContext.Provider>
  );
};
